import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchPublicVocabularyAsync, fetchPublicGroupsAsync } from '../store/slices/vocabularySlice';
import type { VocabularyEntry, VocabularyGroup } from '../store/slices/vocabularySlice';
import PublicLayout from './PublicLayout';
import { formatDateEuropean } from '../utils/dateUtils';
import './PublicVocabulary.css';

const PublicVocabulary: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('group');
  
  const { entries: publicVocabulary, groups: publicGroups, loading: isLoading, error } = useAppSelector(state => state.vocabulary);

  const selectedGroup: VocabularyGroup | undefined =
    groupId && publicGroups.length > 0
      ? publicGroups.find(g => g.id === parseInt(groupId))
      : undefined;

  useEffect(() => {
    // Fetch groups on component mount
    dispatch(fetchPublicGroupsAsync());
  }, [dispatch]);

  useEffect(() => {
    if (groupId && publicGroups.length > 0) {
      dispatch(fetchPublicVocabularyAsync(parseInt(groupId)));
    }
  }, [dispatch, groupId, publicGroups]);

  interface GroupedLesson {
    lesson: NonNullable<VocabularyEntry['lesson']> & { link?: string };
    words: VocabularyEntry[];
  }

  const groupedVocabulary = publicVocabulary.reduce<Record<string, GroupedLesson>>((acc, item) => {
    const lessonDate = formatDateEuropean(item.lesson!.date);
    if (!acc[lessonDate]) {
      acc[lessonDate] = {
        lesson: item.lesson!,
        words: []
      };
    }
    acc[lessonDate].words.push(item);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedVocabulary).sort((a, b) => 
    new Date(groupedVocabulary[b].lesson.date).getTime() - 
    new Date(groupedVocabulary[a].lesson.date).getTime()
  );

  if (!groupId) {
    return (
      <PublicLayout>
        <div className="public-vocabulary-container">
          <div className="groups-selection-card">
            {publicGroups.length > 0 ? (
              <div className="available-groups">
                <h2>Available Groups</h2>
                <div className="groups-list">
                  {publicGroups.map(group => (
                    <div key={group.id} className="group-item">
                      <h4>{group.name}</h4>
                      <p>Level: {group.level}</p>
                      <a href={`/p/vocabulary?group=${group.id}`} className="view-vocabulary-btn">
                        View Vocabulary
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-groups">
                <h2>No Groups Available</h2>
                <p>No vocabulary groups are currently available.</p>
              </div>
            )}
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="public-vocabulary-container">
          <div className="loading-card">
            <div className="loading-spinner"></div>
            <p>Loading vocabulary...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout>
        <div className="public-vocabulary-container">
          <div className="error-card">
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="public-vocabulary-container">
        {selectedGroup && (
          <div className="group-header">
            <h1>Vocabulary for {selectedGroup.name}</h1>
            <div className="group-meta">
              <span className="group-level">{selectedGroup.level}</span>
              <span className="word-count">{publicVocabulary.length} words</span>
            </div>
          </div>
        )}

        {sortedDates.length === 0 ? (
          <div className="no-vocabulary-card">
            <h2>No Vocabulary Yet</h2>
            <p>This group hasn't learned any vocabulary words yet.</p>
          </div>
        ) : (
          <div className="lessons-list">
            {sortedDates.map(date => {
              const lessonData = groupedVocabulary[date];
              return (
                <div key={date} className="lesson-section">
                  <div className="lesson-header">
                    <h2>{date} <span className="lesson-word-count">- ({lessonData.words.length} words)</span></h2>
                    {lessonData.lesson.topic && (
                      <span className="lesson-topic">{lessonData.lesson.topic}</span>
                    )}
                  </div>
                  
                  <ul className="words-list">
                    {lessonData.words.map((item) => (
                      <li key={item.id} className="word-item">
                        {item.word}
                      </li>
                    ))}
                  </ul>
                  
                  {lessonData.lesson.link && (
                    <div className="lesson-link">
                      <a href={lessonData.lesson.link} target="_blank" rel="noopener noreferrer">
                        Lesson Materials
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default PublicVocabulary; 